import React, { useState } from "react";
import Papa from "papaparse";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle, AlertCircle, Play, Download } from "lucide-react";
import { Button } from "../components/ui/button";

const GHL_WEBHOOK_URL = process.env.REACT_APP_GHL_BULK_WEBHOOK_URL || "https://services.leadconnectorhq.com/hooks/Iv5Pr606sl7ct2k6OMlK/webhook-trigger/13fd1b68-641e-4f12-8f03-a5ff143323b7";

const mapCountryToCode = (country) => {
  if (!country) return "";
  const map = {
    "USA": "US",
    "United States": "US",
    "Canada": "CA",
    "United Kingdom": "GB",
    "Australia": "AU",
    "Mexico": "MX",
    "Germany": "DE",
    "France": "FR",
    "Spain": "ES",
    "Italy": "IT",
    "Brazil": "BR",
    "Other": "",
  };
  return map[country] || country;
};

export default function BulkUploadPage() {
  const [file, setFile] = useState(null);
  const [leads, setLeads] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState("");
  const [results, setResults] = useState({ success: 0, failed: 0 });

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;
    setFile(uploadedFile);

    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const parsedLeads = result.data.map(row => {
          // Attempt to flexibly match common column names
          const findCol = (possibleNames) => {
            const key = Object.keys(row).find(k => possibleNames.includes(k.toLowerCase().trim()));
            return key ? row[key] : "";
          };
          
          return {
            firstName: findCol(["first name", "firstname", "first", "first_name"]),
            lastName: findCol(["last name", "lastname", "last", "last_name"]),
            email: findCol(["email", "email address", "email_address"]),
            phone: findCol(["phone", "phone number", "phonenumber", "phone_number"]),
            city: findCol(["city"]),
            state: findCol(["state", "province"]),
            country: findCol(["country"]),
            industry: findCol(["industry", "area of expertise", "industry / area of expertise *", "industry / area of expertise", "expertise", "production focus", "production_focus", "focus"]),
            raw: row
          };
        });
        setLeads(parsedLeads);
      },
      error: (error) => {
        toast.error("Failed to parse CSV file: " + error.message);
      }
    });
  };

  const handleProcessLeads = async () => {
    if (leads.length === 0) {
      toast.error("No valid leads found in the CSV.");
      return;
    }

    setIsUploading(true);
    setProgress(0);
    setResults({ success: 0, failed: 0 });
    
    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < leads.length; i++) {
      const lead = leads[i];
      setStatusText(`Uploading ${i + 1} of ${leads.length}...`);
      
      try {
        const payload = {
          firstName: lead.firstName,
          lastName: lead.lastName,
          email: lead.email,
          phone: lead.phone,
          city: lead.city,
          state: lead.state,
          country: mapCountryToCode(lead.country),
          industry: lead.industry,
          source: "Bulk Upload",
          submittedAt: new Date().toISOString(),
        };

        // Remove empty fields to prevent 422 validation errors from GoHighLevel
        const cleanPayload = Object.fromEntries(
          Object.entries(payload).filter(([_, v]) => v != null && v !== "")
        );

        await axios.post(GHL_WEBHOOK_URL, cleanPayload);
        successCount++;
      } catch (error) {
        failedCount++;
        const serverError = error.response?.data ? JSON.stringify(error.response.data) : error.message;
        console.error("Failed to upload lead:", lead, "Error details:", serverError);
        toast.error(`Failed: ${serverError}`);
      }
      
      setResults({ success: successCount, failed: failedCount });
      setProgress(Math.round(((i + 1) / leads.length) * 100));
    }

    setIsUploading(false);
    setStatusText("Upload complete!");
    toast.success(`Completed! ${successCount} successful, ${failedCount} failed.`);
  };

  const handleDownloadTemplate = () => {
    const headers = "First Name,Last Name,Email,Phone,City,State,Country,Production Focus\n";
    const exampleRow = "John,Doe,john@example.com,+1234567890,Los Angeles,CA,USA,Fixed Indexed Annuities (FIA)\n";
    const blob = new Blob([headers + exampleRow], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "producers_leads_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setFile(null);
    setLeads([]);
    setProgress(0);
    setResults({ success: 0, failed: 0 });
    setStatusText("");
    const fileInput = document.getElementById("csv-upload-input");
    if (fileInput) fileInput.value = "";
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white pt-24 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10 text-center">
          <h1 className="font-display text-4xl md:text-5xl text-[#f5c451] uppercase tracking-wider mb-4">Bulk Leads Upload</h1>
          <p className="text-white/60">Upload a CSV file to import leads into GoHighLevel.</p>
        </div>

        <div className="premium-card p-8 md:p-12 border-t-2 border-[#f5c451]/40 rounded-xl bg-white/[0.02]">
          
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4">1. Upload CSV File</h2>
            <div className="flex items-center justify-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-white/20 border-dashed rounded-lg cursor-pointer bg-black/40 hover:bg-white/5 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-10 h-10 mb-3 text-[#f5c451]" />
                  <p className="mb-2 text-sm text-white/70"><span className="font-semibold text-[#f5c451]">Click to upload</span> or drag and drop</p>
                  <p className="text-xs text-white/50">CSV file containing leads</p>
                </div>
                <input id="csv-upload-input" type="file" accept=".csv" className="hidden" onChange={handleFileUpload} disabled={isUploading} />
              </label>
            </div>
            
            {file && (
              <div className="mt-4 p-4 rounded-md bg-[#14b8a6]/10 border border-[#14b8a6]/30 flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#14b8a6]" />
                <div>
                  <div className="font-semibold text-white">{file.name}</div>
                  <div className="text-xs text-white/60">{(file.size / 1024).toFixed(1)} KB • {leads.length} leads detected</div>
                </div>
              </div>
            )}
          </div>

          <div className="mb-8 p-6 rounded-md bg-white/5 border border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-bold text-[#f5c451] uppercase tracking-wider mb-3">Expected CSV Columns</h3>
              <p className="text-xs text-white/60 leading-relaxed">
                For best results, ensure your CSV has these headers: <br/>
                <span className="text-white">First Name, Last Name, Email, Phone</span> <br/>
                <span className="text-white/40 mt-1 inline-block">Optional: City, State, Country, Production Focus</span>
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleDownloadTemplate}
              className="border-white/20 text-white bg-transparent hover:bg-white/10 hover:text-white"
            >
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
          </div>

          {leads.length > 0 && (
            <div className="border-t border-white/10 pt-8 mt-8">
              <h2 className="text-xl font-bold mb-6">2. Process Upload</h2>
              
              {!isUploading && progress === 0 && (
                <Button 
                  onClick={handleProcessLeads}
                  className="w-full h-14 bg-[#f5c451] hover:bg-amber-400 text-black font-bold uppercase tracking-wider text-lg"
                >
                  <Play className="w-5 h-5 mr-2" /> Start Bulk Upload to GHL
                </Button>
              )}

              {(isUploading || progress > 0) && (
                <div className="space-y-4">
                  <div className="flex justify-between items-end mb-2">
                    <span className="text-sm font-medium text-white/80">{statusText}</span>
                    <span className="text-sm font-bold text-[#f5c451]">{progress}%</span>
                  </div>
                  <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#f5c451] to-amber-300 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="p-4 rounded-lg bg-[#14b8a6]/10 border border-[#14b8a6]/20 flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-[#14b8a6]" />
                      <div>
                        <div className="text-2xl font-bold text-[#14b8a6]">{results.success}</div>
                        <div className="text-xs text-white/60 uppercase tracking-wider">Successful</div>
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                      <AlertCircle className="w-6 h-6 text-red-400" />
                      <div>
                        <div className="text-2xl font-bold text-red-400">{results.failed}</div>
                        <div className="text-xs text-white/60 uppercase tracking-wider">Failed</div>
                      </div>
                    </div>
                  </div>

                  {!isUploading && progress === 100 && (
                    <div className="mt-8 pt-4 border-t border-white/10">
                      <Button 
                        onClick={handleReset}
                        className="w-full h-12 bg-white/10 hover:bg-white/20 text-white font-bold uppercase tracking-wider"
                      >
                        <Upload className="w-4 h-4 mr-2" /> Upload Another File
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
